import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { useNotification } from 'config/Notification';
import {
    fetchStandards as fetchStandardsService,
    fetchControlsByStandardId as fetchControlsService,
    updateControlsweightages as updateControlWeightagesService,
} from 'services/modules/cap/standardservice';
import Breadcrumbs from 'config/Breadcrumb';

// ─── Helpers (identical to admin version) ────────────────────────────────────

const getControlHealth = (control) => {
    const stats = control._subcontrolStats;
    if (!control.status) return 'inactive';
    if (!stats) return 'ok';
    if (stats.active_subcontrols === 0) return 'no_questions';
    if (stats.scored_subcontrols === 0) return 'all_info';
    const infoRatio = stats.active_subcontrols > 0
        ? stats.informational_subcontrols / stats.active_subcontrols : 0;
    if (infoRatio > 0.5) return 'high_info';
    return 'ok';
};

const HEALTH_CONFIG = {
    inactive:     { label: 'Inactive control',           color: 'default', icon: <LockOutlinedIcon sx={{ fontSize: 14 }} />,     tooltip: 'This control is inactive and cannot receive a weightage.',                                                                  blocked: true  },
    no_questions: { label: 'No active questions',         color: 'error',   icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,     tooltip: 'This control has no active sub-controls or questions. Add active questions before assigning a weightage.',                 blocked: true  },
    all_info:     { label: 'All questions informational', color: 'error',   icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,     tooltip: 'Every active question is informational and will never be scored. Assigning a weightage has no effect.',                    blocked: true  },
    high_info:    { label: 'High informational ratio',    color: 'warning', icon: <WarningAmberIcon sx={{ fontSize: 14 }} />,     tooltip: 'More than 50% of active questions are informational. The scored portion will be smaller than its weightage suggests.',      blocked: false },
    ok:           { label: null,                          color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />, tooltip: 'All active questions contribute to the score.',                                                                          blocked: false },
};

const ControlHealthBadge = ({ control }) => {
    const health = getControlHealth(control);
    const cfg = HEALTH_CONFIG[health];
    if (!cfg || health === 'ok') return null;
    return (
        <Tooltip title={cfg.tooltip} placement="right" arrow>
            <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" variant="outlined"
                sx={{ ml: 1, fontSize: '11px', height: 22 }} />
        </Tooltip>
    );
};

const InformationalWarningPanel = ({ controls }) => {
    const warnings = controls.filter(c => ['high_info','all_info'].includes(getControlHealth(c)));
    if (warnings.length === 0) return null;
    return (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <strong>Informational question imbalance detected:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                {warnings.map(c => {
                    const stats = c._subcontrolStats;
                    const health = getControlHealth(c);
                    return (
                        <li key={c._id} style={{ fontSize: 13 }}>
                            <strong>{c.Title}</strong>
                            {health === 'all_info'
                                ? ' — ALL active questions are informational. This control cannot be scored.'
                                : ` — ${stats?.informational_subcontrols} of ${stats?.active_subcontrols} active questions are informational (${Math.round((stats?.informational_subcontrols / stats?.active_subcontrols) * 100)}%).`
                            }
                        </li>
                    );
                })}
            </ul>
        </Alert>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const ConfigureControlsWeightages = () => {
    const standardTypes = [
        { id: 'standard-based', name: 'Standard Based' },
        { id: 'domain-based',   name: 'Domain Based'   },
    ];

    const { openNotification } = useNotification();

    const [standardType,      setStandardType]      = useState(standardTypes[0].id);
    const [selectedStandards, setSelectedStandards] = useState([]);
    const [controls,          setControls]          = useState([]);
    const [selectedStandard,  setSelectedStandard]  = useState('');

    const eligibleControls = useMemo(() =>
        controls.filter(c => !['inactive','no_questions','all_info'].includes(getControlHealth(c))), [controls]);
    const blockedControls = useMemo(() =>
        controls.filter(c => ['inactive','no_questions','all_info'].includes(getControlHealth(c))), [controls]);

    const fetchStandardsData = async () => {
        try {
            const group = standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard';
            const response = await fetchStandardsService( group );
            setSelectedStandards(response.data);
        } catch { openNotification("Error while fetching standards", 'error'); }
    };

    const fetchControlsData = async (standard) => {
        try {
            setControls([]);
            const response = await fetchControlsService({ standard: standard.stdid || standard.id });
            setControls(response.data || []);
        } catch { openNotification("Error while fetching controls", 'error'); }
    };

    const validateTotalWeightage = (weightages) => {
        const eligibleIds = new Set(eligibleControls.map(c => c._id || c.id));
        const total = Math.round(
            weightages.filter(w => eligibleIds.has(w.id))
                      .reduce((acc, w) => acc + parseFloat(w.weight || 0), 0) * 100
        ) / 100;
        if (total < 100) return `Total weightage of eligible controls is ${total}% — must be exactly 100%.`;
        if (total > 100) return `Total weightage of eligible controls is ${total}% — must not exceed 100%.`;
        return null;
    };

    const formikStep2 = useFormik({
        initialValues: {
            standard:  selectedStandard,
            weightages: controls.map((c) => ({
                id:        c._id || c.id,
                controlID: c.ControlID,
                control:   c.Title,
                weight:    c.Weightage || 0,
            })),
        },
        validationSchema: Yup.object({
            standard:  Yup.object({ id: Yup.string().required() }).required(),
            weightages: Yup.array().of(Yup.object().shape({
                id:     Yup.string().required(),
                weight: Yup.number().required().min(0).max(100)
                    .test('is-decimal', 'Must be valid decimal', v => /^\d+(\.\d{1,2})?$/.test(v)),
            })),
        }),
        onSubmit: async (values) => {
            if (standardType === 'standard-based') { openNotification("Standard-based weightages are auto-calculated", 'info'); return; }
            const error = validateTotalWeightage(values.weightages);
            if (error) { openNotification(error, 'error'); return; }
            try {
                const blockedIds = new Set(blockedControls.map(c => c._id || c.id));
                const bodylist = values.weightages.map(e => ({
                    id: e.id, ControlID: e.controlID,
                    Weightage: blockedIds.has(e.id) ? 0 : e.weight,
                }));
                const response = await updateControlWeightagesService(values.standard.id, { body: bodylist });
                if (response.status === 200) {
                    openNotification("Control weightages updated successfully", 'success');
                    fetchControlsData(selectedStandard);
                }
            } catch { openNotification("Error while updating weightages", 'error'); }
        },
        enableReinitialize: true,
    });

    const liveTotal = useMemo(() => {
        const eligibleIds = new Set(eligibleControls.map(c => c._id || c.id));
        return Math.round(
            formikStep2.values.weightages
                .filter(w => eligibleIds.has(w.id))
                .reduce((acc, w) => acc + parseFloat(w.weight || 0), 0) * 100
        ) / 100;
    }, [formikStep2.values.weightages, eligibleControls]);

    const totalColor = liveTotal === 100 ? 'success' : liveTotal > 100 ? 'error' : 'warning';

    useEffect(() => { setControls([]); fetchStandardsData(); }, [standardType]);
    useEffect(() => { if (selectedStandard) fetchControlsData(selectedStandard); }, [selectedStandard]);

    return (
        <div className='main-cont'>
            <Card>
                <CardHeader title={
                    <div className="card-head">
                        <div className="flex-head">
                            <label className="label">
                                <Breadcrumbs paths={['CAP Configurator', 'Configure Control Weightages']} />
                            </label>
                        </div>
                        <Button variant="contained" className="back font" onClick={() => window.history.back()}>Back</Button>
                    </div>
                } />
                <CardContent>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Select Standard Type</FormLabel>
                        <RadioGroup value={standardType} onChange={(e) => setStandardType(e.target.value)} className='d-flex flex-row gap-2'>
                            {standardTypes.map((t) => (
                                <FormControlLabel key={t.id} value={t.id} control={<Radio />} label={t.name} />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <Divider className='my-1' />

                    {standardType && (
                        <CardContent className='loader-container'>
                            <form onSubmit={formikStep2.handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Standard</InputLabel>
                                            <Select
                                                label="Standard"
                                                value={formikStep2.values.standard}
                                                onChange={(e) => {
                                                    setSelectedStandard(e.target.value);
                                                    formikStep2.setFieldValue('standard', e.target.value);
                                                }}
                                            >
                                                {selectedStandards.map((s) => (
                                                    <MenuItem key={s.id} value={s}>{s.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {controls.length > 0 && (
                                    <>
                                        <InformationalWarningPanel controls={controls} />

                                        {blockedControls.length > 0 && (
                                            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                                                <strong>{blockedControls.length} control{blockedControls.length > 1 ? 's are' : ' is'} excluded from weightage:</strong>
                                                {' '}{blockedControls.map(c => c.Title).join(', ')}.
                                                {' '}These will be saved with weightage 0. The remaining {eligibleControls.length} eligible control{eligibleControls.length !== 1 ? 's' : ''} must total 100%.
                                            </Alert>
                                        )}

                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <Typography variant="body2" color="text.secondary">Eligible controls total:</Typography>
                                            <Chip
                                                label={`${liveTotal}% / 100%`}
                                                color={totalColor}
                                                size="small"
                                                variant={liveTotal === 100 ? 'filled' : 'outlined'}
                                            />
                                        </Box>

                                        {formikStep2.values.weightages.map((weightage, index) => {
                                            const control = controls.find(c => (c._id || c.id) === weightage.id);
                                            const health  = control ? getControlHealth(control) : 'ok';
                                            const cfg     = HEALTH_CONFIG[health];
                                            const blocked = cfg.blocked;
                                            const stats   = control?._subcontrolStats;

                                            return (
                                                <Box key={weightage.id} mb={2} p={1.5} sx={{
                                                    borderRadius: 2, border: '1px solid',
                                                    borderColor: blocked ? 'error.light' : health === 'high_info' ? 'warning.light' : 'divider',
                                                    background:  blocked ? '#fff5f5' : health === 'high_info' ? '#fffbf0' : 'transparent',
                                                    opacity: blocked ? 0.75 : 1,
                                                }}>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={5}>
                                                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                                                                <Typography variant="body1" color={blocked ? 'text.disabled' : 'text.primary'}>
                                                                    {weightage.control}
                                                                </Typography>
                                                                <ControlHealthBadge control={control} />
                                                            </Box>
                                                            {stats && (
                                                                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                                                                    {stats.active_subcontrols} active question{stats.active_subcontrols !== 1 ? 's' : ''}
                                                                    {stats.informational_subcontrols > 0 && (
                                                                        <span style={{ color: health === 'all_info' ? '#c62828' : '#f57c00' }}>
                                                                            {' '}· {stats.informational_subcontrols} informational
                                                                        </span>
                                                                    )}
                                                                    {stats.scored_subcontrols > 0 && (
                                                                        <span style={{ color: '#2e7d32' }}>{' '}· {stats.scored_subcontrols} scored</span>
                                                                    )}
                                                                </Typography>
                                                            )}
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                label="Weight %"
                                                                name={`weightages[${index}].weight`}
                                                                disabled={blocked || standardType === 'standard-based'}
                                                                value={blocked ? 0 : formikStep2.values.weightages[index]?.weight}
                                                                onChange={formikStep2.handleChange}
                                                                fullWidth margin="normal"
                                                                inputProps={{ step: '0.1', min: 0, max: 100 }}
                                                                sx={{ '& .MuiInputBase-root': { background: blocked ? '#f5f5f5' : undefined } }}
                                                            />
                                                        </Grid>
                                                        {blocked && (
                                                            <Grid item xs={4}>
                                                                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                                                                    <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                                    <Typography variant="caption" color="text.disabled">{cfg.tooltip}</Typography>
                                                                </Box>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            );
                                        })}

                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Button color="primary" variant="contained" type="submit"
                                                disabled={standardType === 'standard-based'}>
                                                Save Weightages
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </form>
                        </CardContent>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ConfigureControlsWeightages;